import { faPlus, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../Icon";
import { tipAttrs } from "../../tooltip";
import { type Combatant, type EncounterCommand, type EncounterState, hasStatus, HIDDEN_STATUS, sideOf } from "@lancer-vtt/domain";
import { nameGlyph } from "../../../routes/home/combatCells";

/**
 * The UNITS window body — the encounter's combatants as two
 * selectable sections: **Players** (the `pc` side — mechs/pilots, controller-owned) on top,
 * **Enemies** (the `npc` side — GM-owned) below. Each row is glyph + name + a "not on board"
 * marker for unplaced units + a remove ✕; clicking the row selects it (selecting an unplaced unit
 * you control arms first-placement on the canvas, via the host's shared selection). Each section
 * header carries an **ADD** affordance that opens the lighter DEPLOY picker (ADD MECH + ADD PILOT for
 * players, ADD ENEMY for enemies — the rail's DEPLOY moved here). Control is
 * mirrored as an affordance (a row you can't command is inert); the server gate stays the wall.
 */
export function UnitsBody({
  state,
  actorId,
  selectedId,
  disabled,
  onSelect,
  canControl,
  dispatch,
  onAddMech,
  onAddPilot,
  onAddEnemy,
}: {
  state: EncounterState;
  actorId: string;
  selectedId: string | null;
  disabled: boolean;
  onSelect: (id: string | null) => void;
  canControl: (c: Combatant) => boolean;
  dispatch: (command: EncounterCommand) => unknown;
  onAddMech: () => void;
  onAddPilot: () => void;
  onAddEnemy: () => void;
}) {
  const combatants = Object.values(state.combatants);
  const players = combatants.filter((c) => sideOf(c) === "pc");
  const enemies = combatants.filter((c) => sideOf(c) === "npc");

  const remove = (c: Combatant) => {
    dispatch({ type: "remove_combatant", actorId, combatantId: c.id });
    if (c.id === selectedId) onSelect(null);
  };

  return (
    <div className="gms-content cw-units">
      <UnitSection
        title="Players"
        addLabel="Add mech"
        onAdd={onAddMech}
        addLabel2="Add pilot"
        onAdd2={onAddPilot}
        addDisabled={disabled}
        units={players}
        empty="No player mechs yet."
        selectedId={selectedId}
        disabled={disabled}
        onSelect={onSelect}
        canControl={canControl}
        onRemove={remove}
      />
      <UnitSection
        title="Enemies"
        addLabel="Add enemy"
        onAdd={onAddEnemy}
        addDisabled={disabled}
        units={enemies}
        empty="No enemies yet."
        selectedId={selectedId}
        disabled={disabled}
        onSelect={onSelect}
        canControl={canControl}
        onRemove={remove}
      />
    </div>
  );
}

/**
 * One side's roster section in the UNITS window (Players / Enemies) — a labelled list + an ADD
 * affordance. `addLabel2`/`onAdd2` is an optional SECOND add button (Players carries both ADD MECH
 * and ADD PILOT; Enemies carries only ADD ENEMY, so it's omitted there).
 */
function UnitSection({
  title,
  addLabel,
  onAdd,
  addLabel2,
  onAdd2,
  addDisabled,
  units,
  empty,
  selectedId,
  disabled,
  onSelect,
  canControl,
  onRemove,
}: {
  title: string;
  addLabel: string;
  onAdd: () => void;
  addLabel2?: string;
  onAdd2?: () => void;
  addDisabled: boolean;
  units: Combatant[];
  empty: string;
  selectedId: string | null;
  disabled: boolean;
  onSelect: (id: string | null) => void;
  canControl: (c: Combatant) => boolean;
  onRemove: (c: Combatant) => void;
}) {
  return (
    <section className="cw-unit-section">
      <div className="cw-section-head">
        <span className="cw-section-name">{title}</span>
        <button type="button" className="cw-add" disabled={addDisabled} onClick={onAdd}>
          <Icon icon={faPlus} className="icon-lead" />{addLabel}
        </button>
        {onAdd2 && (
          <button type="button" className="cw-add" disabled={addDisabled} onClick={onAdd2}>
            <Icon icon={faPlus} className="icon-lead" />{addLabel2}
          </button>
        )}
      </div>
      {units.length === 0 ? (
        <div className="cw-hint">{empty}</div>
      ) : (
        <ul className="cw-unit-list">
          {units.map((c) => {
            const controllable = !disabled && canControl(c);
            const isSelected = c.id === selectedId;
            return (
              <li key={c.id} className="cw-unit-row">
                <button
                  type="button"
                  className={`cw-unit${isSelected ? " cw-unit-selected" : ""}`}
                  disabled={!controllable}
                  aria-pressed={isSelected}
                  onClick={() => onSelect(isSelected ? null : c.id)}
                >
                  <span className="cw-unit-glyph">{nameGlyph(c.name)}</span>
                  <span className="cw-unit-name">{c.name}</span>
                  {/* Fog-of-war indicator: a concealed unit only ever REACHES a client
                      that may see it (the server projects it out of player payloads), so this tag
                      needs no role gate — if the row renders concealed, the viewer is the GM (or
                      the unit's owner) and the tag honestly reads "players can't see this". */}
                  {c.concealed && <span className="cw-unit-tag cw-unit-tag-concealed">concealed</span>}
                  {/* RAW Hidden: every viewer sees the unit, so the tag carries the board's holo-ghost look. */}
                  {hasStatus(c.statuses, HIDDEN_STATUS) && <span className="cw-unit-tag status-holo">hidden</span>}
                  {c.pos == null && <span className="cw-unit-tag">not on board</span>}
                </button>
                {isSelected && controllable && (
                  <button
                    type="button"
                    className="cw-unit-remove"
                    aria-label={`remove ${c.name}`}
                    {...tipAttrs({ label: `Remove ${c.name}`, hint: "take it out of the encounter" })}
                    onClick={() => onRemove(c)}
                  >
                    <Icon icon={faTrashCan} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
